import React from "react";
import { Alert, AlertTitle, Portal, Snackbar } from "@mui/material";

/**
 * NotificationSnackbar
 *
 * App-wide toast: a MUI Snackbar wrapping an Alert. Defaults match the most
 * common case (error, top-center, dismissible); every aspect is overridable so
 * the existing call sites (success/info, auto-hide, custom anchor/styling) map
 * onto it without behavior changes.
 *
 * Rendered through a Portal to document.body so it escapes any parent stacking
 * context (e.g. the VesselPanel container at position:absolute; z-index:1000,
 * which otherwise traps the snackbar below the fixed AppBar). MUI Snackbar's
 * default z-index (1400) then sits above the AppBar (drawer + 1 = 1201).
 *
 * Open state is controlled by the caller via `open` + `onClose`.
 */
export default function NotificationSnackbar({
  open,
  message,
  severity = "error",
  title,
  onClose,
  autoHideDuration = null,
  anchorOrigin = { vertical: "top", horizontal: "center" },
  showCloseButton = true,
  variant,
  alertSx,
  style,
  ...snackbarProps
}) {
  return (
    <Portal>
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        // z-index as an inline style (highest specificity, beats any Emotion
        // class ordering) so the toast always sits above the fixed AppBar
        // (z-index 1201) and the map panels (~1000–1150). Combined with the
        // Portal to document.body, this escapes the VesselPanel stacking context
        // (position:absolute; z-index:1000) that otherwise traps it underneath.
        style={{ zIndex: 1500, ...style }}
        {...snackbarProps}
      >
        <Alert
          severity={severity}
          variant={variant}
          onClose={showCloseButton ? onClose : undefined}
          sx={{ width: "100%", ...alertSx }}
        >
          {title ? <AlertTitle>{title}</AlertTitle> : null}
          {message}
        </Alert>
      </Snackbar>
    </Portal>
  );
}
